import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useCurrentUser } from "../api/useCurrentUser";
import { useReportData } from "./useReportData";
import { CohortFilterBar } from "./components/CohortFilterBar";
import { StateLegend } from "./components/StateLegend";
import { PopulationView } from "./PopulationView";
import { TePouView } from "./TePouView";
import { DomainView } from "./DomainView";
import { ServicesView } from "./ServicesView";
import { ActivityView } from "./ActivityView";
import { SnapshotView } from "./SnapshotView";
import { WhanauStatesView } from "./WhanauStatesView";
import { ToCView } from "./ToCView";
import { WordExportButton } from "./WordExportButton";
import { EMPTY_FILTERS, COHORT_DIMENSIONS } from "./reportTypes";
import type { CohortDimension, CohortFilters } from "./reportTypes";
import "./reporting.css";
import "./reportingFilters.css";

/**
 * /reports — the Whānau Factor reporting dashboard.
 *
 * Super-admin only. The client-side gate here is UX; the server is authoritative
 * (the /reports/aggregates endpoint returns 403 for everyone else, and only ever
 * returns de-identified aggregates — never raw whānau rows). Mirrors the gate
 * pattern in AuditLog.
 *
 * PREVIEW_MODE (dev only): VITE_REPORTS_USE_MOCK=true renders the dashboard on
 * the bundled sample data with NO backend — it bypasses the /me role gate so the
 * views can be previewed and demoed without the API running. It is compiled out
 * of any production build (guarded by import.meta.env.DEV).
 */
type Tab =
  | "population"
  | "pou"
  | "domain"
  | "services"
  | "activity"
  | "snapshot"
  | "whanauStates"
  | "toc";

const TABS: readonly Tab[] = [
  "population",
  "snapshot",
  "whanauStates",
  "toc",
  "pou",
  "domain",
  "services",
  "activity",
];

const PREVIEW_MODE =
  import.meta.env.DEV &&
  (import.meta.env.VITE_REPORTS_USE_MOCK as string | undefined) === "true";

export function ReportsPage() {
  const { t } = useTranslation();
  const currentUser = useCurrentUser();

  const [filters, setFilters] = useState<CohortFilters>(EMPTY_FILTERS);
  const [tab, setTab] = useState<Tab>("population");

  const report = useReportData(filters);

  const toggle = useCallback((dim: CohortDimension, value: string) => {
    setFilters((prev) => {
      const arr = prev[dim] as string[];
      const next = arr.includes(value)
        ? arr.filter((v) => v !== value)
        : [...arr, value];
      // `dim` is a computed key spanning string[] and DurationBand[] value types;
      // the runtime value is always valid for the chosen dimension.
      return { ...prev, [dim]: next } as CohortFilters;
    });
  }, []);

  const clear = useCallback(() => setFilters(EMPTY_FILTERS), []);

  const activeChips = COHORT_DIMENSIONS.flatMap((dim) =>
    (filters[dim] as string[]).map((value) => ({
      dim,
      value,
      label: dim === "durationBand" ? t(`reports.duration.${value}`) : value,
    })),
  );

  const canView =
    PREVIEW_MODE ||
    (currentUser.kind === "ok" &&
      (currentUser.data.permissions?.["reports.view"] ??
        currentUser.data.role.roleId === "super-admin"));

  const forbidden =
    !PREVIEW_MODE &&
    ((currentUser.kind === "ok" && !canView) || report.kind === "forbidden");

  const body = useMemo(() => {
    if (report.kind !== "ok") return null;
    const agg = report.data;
    switch (tab) {
      case "population":
        return <PopulationView agg={agg} />;
      case "pou":
        return <TePouView agg={agg} filters={filters} />;
      case "domain":
        return <DomainView agg={agg} />;
      case "services":
        return <ServicesView agg={agg} />;
      case "activity":
        return <ActivityView filters={filters} />;
      case "snapshot":
        return <SnapshotView filters={filters} />;
      case "whanauStates":
        return <WhanauStatesView agg={agg} filters={filters} />;
      case "toc":
        return <ToCView agg={agg} />;
    }
  }, [report, tab, filters]);

  return (
    <article className="rpt" aria-labelledby="rpt-title">
      <header className="rpt__header">
        <h1 id="rpt-title" className="rpt__title">
          {t("reports.title")}
        </h1>
        <p className="rpt__subtitle">{t("reports.subtitle")}</p>
      </header>

      {!PREVIEW_MODE && currentUser.kind === "loading" && (
        <p className="rpt__status">{t("reports.loading")}</p>
      )}

      {forbidden && (
        <p className="rpt__status rpt__status--error">
          {t("reports.forbidden")}
        </p>
      )}

      {!forbidden && !PREVIEW_MODE && currentUser.kind === "error" && (
        <p className="rpt__status rpt__status--error">
          {t("reports.error", { message: currentUser.message })}
        </p>
      )}

      {!forbidden && canView && (
        <>
          {report.kind === "loading" && (
            <p className="rpt__status">{t("reports.loading")}</p>
          )}

          {report.kind === "error" && (
            <p className="rpt__status rpt__status--error">
              {t("reports.error", { message: report.message })}
            </p>
          )}

          {report.kind === "ok" && (
            <div className="rpt-layout">
              <CohortFilterBar
                facets={report.data.facets}
                filters={filters}
                onToggle={toggle}
                onClear={clear}
              />

              <div className="rpt-main">
                {report.source === "mock" && (
                  <p className="rpt__notice" role="status">
                    {t("reports.mockNotice")}
                  </p>
                )}

                {activeChips.length > 0 && (
                  <div
                    className="rpt-active"
                    aria-label={t("reports.activeFilters")}
                  >
                    <span className="rpt-active__label">
                      {t("reports.activeFilters")}
                    </span>
                    {activeChips.map((c) => (
                      <button
                        key={`${c.dim}:${c.value}`}
                        type="button"
                        className="rpt-active__chip"
                        onClick={() => toggle(c.dim, c.value)}
                        aria-label={t("reports.removeFilter", {
                          value: c.label,
                        })}
                      >
                        {c.label} <span aria-hidden="true">✕</span>
                      </button>
                    ))}
                    <button
                      type="button"
                      className="rpt-active__clear"
                      onClick={clear}
                    >
                      {t("reports.clearAll", { count: activeChips.length })}
                    </button>
                  </div>
                )}

                {report.data.suppressed ? (
                  <p className="rpt__status">
                    {t("reports.suppressed", { n: report.data.totalWhanau })}
                  </p>
                ) : (
                  <>
                    <div className="rpt__meta">
                      <span className="rpt__count">
                        {t("reports.cohortCount", {
                          n: report.data.totalWhanau,
                        })}
                      </span>
                      <StateLegend />
                      <WordExportButton agg={report.data} />
                    </div>

                    <nav
                      className="rpt__tabs"
                      aria-label={t("reports.viewsLabel")}
                    >
                      {TABS.map((tabId) => (
                        <button
                          key={tabId}
                          type="button"
                          role="tab"
                          aria-selected={tab === tabId}
                          className={
                            tab === tabId
                              ? "rpt__tab rpt__tab--active"
                              : "rpt__tab"
                          }
                          onClick={() => setTab(tabId)}
                        >
                          {t(`reports.tab.${tabId}`)}
                        </button>
                      ))}
                    </nav>

                    {report.data.totalWhanau === 0 ? (
                      <p className="rpt__status">{t("reports.noCohort")}</p>
                    ) : (
                      body
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </article>
  );
}

export default ReportsPage;
